import { useQuery } from "@tanstack/react-query";
import { complaintsApi } from "@/api/complaints.api";
import { excelTranslationsApi } from "@/api/excel-translations.api";
import { payoutsApi } from "@/api/payouts.api";
import { testsApi } from "@/api/tests.api";
import { wordApi } from "@/api/word.api";
import { useAdminScope } from "@/hooks/use-admin-scope";
import { useApp } from "@/hooks/use-app-context";
import { EXCEL_FILES_QUERY_KEY } from "@/pages/admin/uploads-excels/excel-files.constants";
import { countExcelImports } from "@/pages/admin/uploads-excels/excel-file-monitor";
import { openComplaints, sumCounts, type TodoCounts } from "./todo-counts";

// Counters move slowly and feed both the dashboard and the menu badges:
// one fetch per minute is plenty and the two share the cache.
const STALE = 60_000;

/**
 * What waits on the admin, read from the endpoints the queue pages already
 * use. Nothing here is computed client-side beyond counting.
 */
export function useAdminTodo(): { counts: TodoCounts; isFetching: boolean } {
  const { user } = useApp();
  const { isSuperAdmin, accessibleLanguages, isLoading: scopeLoading } = useAdminScope();
  const signedIn = Boolean(user?.id);
  const languageCodes = accessibleLanguages.map((language) => language.code).filter(Boolean);

  const payments = useQuery({
    queryKey: ["admin-todo", "payments"],
    queryFn: () => payoutsApi.getPayoutRequests("PENDING"),
    select: (response) => response.items.length,
    enabled: signedIn,
    staleTime: STALE,
  });

  // Pending submissions are counted server side; one row is enough to read the total.
  const tests = useQuery({
    queryKey: ["admin-todo", "tests"],
    queryFn: () => testsApi.getPendingSubmissions(1, 1),
    select: (response) => response.total,
    enabled: signedIn,
    staleTime: STALE,
  });

  const complaints = useQuery({
    queryKey: ["admin-todo", "complaints"],
    queryFn: () => complaintsApi.getComplaintsStats(),
    select: openComplaints,
    enabled: signedIn,
    staleTime: STALE,
  });

  // The review page works one language at a time: a scoped admin only counts
  // the languages they may review, a super admin counts them all at once.
  const translations = useQuery({
    queryKey: ["admin-todo", "translations", isSuperAdmin ? "all" : languageCodes.join(",")],
    queryFn: async ({ signal }) => {
      if (isSuperAdmin) return (await excelTranslationsApi.getProposals(undefined, "pending", 1, 1, signal)).total;
      const pages = await Promise.all(
        languageCodes.map((code) => excelTranslationsApi.getProposals(code, "pending", 1, 1, signal)),
      );
      return sumCounts(pages.map((page) => page.total)) ?? 0;
    },
    enabled: signedIn && !scopeLoading,
    staleTime: STALE,
  });

  // Same key and request as the phrase files tab, so they share one fetch.
  const phraseFiles = useQuery({
    queryKey: EXCEL_FILES_QUERY_KEY,
    queryFn: wordApi.getInitialDataFiles,
    select: (files) => countExcelImports(files).pending,
    enabled: signedIn && isSuperAdmin,
    staleTime: STALE,
  });

  return {
    counts: {
      payments: payments.data,
      tests: tests.data,
      translations: translations.data,
      complaints: complaints.data,
      phraseFiles: isSuperAdmin ? phraseFiles.data : undefined,
    },
    isFetching: payments.isFetching || tests.isFetching || complaints.isFetching || translations.isFetching,
  };
}
